import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { adminApi } from '@/services/api';
import { toast } from 'sonner';
import ImageUploadCrop from '@/components/admin/ImageUploadCrop';
import { resolveImage, PRODUCT_IMAGE_BASE } from '@/lib/imageUrl';

// Variation Manager Component
const VariationManager = ({ productId, productName }) => {
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ codigo: '', descripcion: '', activo: true, orden: 99 });

  useEffect(() => { loadVariations(); }, [productId]);

  const loadVariations = async () => {
    try {
      const res = await adminApi.getVariations(productId);
      setVariations(res.data);
    } catch { toast.error('Error al cargar variaciones'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ codigo: '', descripcion: '', activo: true, orden: 99 }); setEditingId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.descripcion.trim()) { toast.error('Código y descripción son requeridos'); return; }
    try {
      if (editingId) {
        await adminApi.updateVariation(productId, editingId, form);
        toast.success('Variación actualizada');
      } else {
        await adminApi.createVariation(productId, form);
        toast.success('Variación creada');
      }
      resetForm();
      loadVariations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (varId) => {
    if (!window.confirm('¿Eliminar esta variación?')) return;
    try {
      await adminApi.deleteVariation(productId, varId);
      toast.success('Variación eliminada');
      loadVariations();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="bg-gray-50 p-4 border-t border-gray-200" data-testid={`variations-panel-${productId}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-steel-dark text-sm">Variaciones de {productName} ({variations.length})</h4>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="text-sm bg-accent-primary text-white px-3 py-1 rounded hover:bg-accent-hover transition-colors flex items-center gap-1" data-testid={`add-variation-${productId}`}>
          <Plus size={14} /> Agregar
        </button>
      </div>

      {/* Add/Edit Form */}
      {(showForm || editingId) && (
        <div className="bg-white p-4 rounded border border-gray-200 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-steel-gray mb-1">Código único *</label>
              <input type="text" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                placeholder="Ej: SOL-001" data-testid="variation-codigo-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-steel-gray mb-1">Descripción *</label>
              <input type="text" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                placeholder="Ej: Medida 3/8" data-testid="variation-desc-input" />
            </div>
            <div className="flex items-end gap-2">
              <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 flex items-center gap-1" data-testid="variation-save-btn">
                <Save size={14} /> {editingId ? 'Actualizar' : 'Crear'}
              </button>
              <button onClick={resetForm} className="text-steel-gray px-3 py-2 text-sm hover:text-steel-dark">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variations List */}
      {loading ? (
        <p className="text-sm text-steel-gray">Cargando...</p>
      ) : variations.length === 0 ? (
        <p className="text-sm text-steel-gray italic">Sin variaciones. Agrega medidas/códigos para este producto.</p>
      ) : (
        <div className="space-y-1">
          {variations.map(v => (
            <div key={v.id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-gray-100 text-sm group hover:border-accent-primary/30" data-testid={`variation-row-${v.id}`}>
              <div className="flex items-center gap-3">
                <span className="font-mono text-accent-primary font-bold bg-accent-primary/10 px-2 py-0.5 rounded text-xs">{v.codigo}</span>
                <span className="text-steel-dark">{v.descripcion}</span>
                {!v.activo && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Inactivo</span>}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setForm({ codigo: v.codigo, descripcion: v.descripcion, activo: v.activo, orden: v.orden }); setEditingId(v.id); setShowForm(false); }}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(v.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const initialProductForm = {
  nombre: '',
  codigo: '',
  descripcion: '',
  seo_url: '',
  ruta_img: '',
  galeria: '',
  orden: 99,
  family_id: '',
  activo: true,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState(initialProductForm);

  useEffect(() => {
    loadProducts();
    loadFamilies();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await adminApi.getProducts();
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilies = async () => {
    try {
      const res = await adminApi.getFamilies();
      setFamilies(res.data.filter(f => f.activo));
    } catch { /* silencio - no critico */ }
  };

  const handleEdit = (product) => {
    setFormData({
      ...initialProductForm,
      ...product,
      family_id: product.family_id ?? '',
    });
    setEditingId(product.id);
    setShowForm(true);
  };

  const handleCreate = () => {
    setFormData(initialProductForm);
    setEditingId(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.nombre) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.updateProduct(editingId, formData);
        toast.success('Producto actualizado');
      } else {
        await adminApi.createProduct(formData);
        toast.success('Producto creado');
      }
      setShowForm(false);
      loadProducts();
    } catch (error) {
      toast.error('Error al guardar el producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await adminApi.deleteProduct(id);
      toast.success('Producto eliminado');
      loadProducts();
    } catch (error) {
      toast.error('Error al eliminar el producto');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-steel-dark uppercase tracking-wider">
          Productos
        </h1>
        <button onClick={handleCreate} className="btn-primary flex items-center gap-2">
          <Plus size={20} />
          Nuevo Producto
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg">
                {editingId ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-steel-dark mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="input-industrial"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-steel-dark mb-1">Codigo de Producto</label>
                  <input
                    type="text"
                    value={formData.codigo || ''}
                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                    className="input-industrial"
                    placeholder="Solo si no tiene variaciones"
                    data-testid="product-codigo-input"
                  />
                  <p className="text-xs text-steel-gray mt-1">Para productos unicos sin variaciones de medida</p>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-steel-dark mb-1">SEO URL</label>
                  <input
                    type="text"
                    value={formData.seo_url}
                    onChange={(e) => setFormData({ ...formData, seo_url: e.target.value })}
                    className="input-industrial"
                    placeholder="auto-generado"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-steel-dark mb-1">Descripción</label>
                <textarea
                  rows={4}
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="input-industrial resize-none"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <ImageUploadCrop
                  label="Imagen Principal"
                  value={formData.ruta_img}
                  onChange={(path) => setFormData({ ...formData, ruta_img: path })}
                  folder="products"
                  legacyBase={PRODUCT_IMAGE_BASE}
                />
                <ImageUploadCrop
                  label="Imagen Galería"
                  value={formData.galeria}
                  onChange={(path) => setFormData({ ...formData, galeria: path })}
                  folder="products"
                  legacyBase={PRODUCT_IMAGE_BASE}
                />
              </div>
              
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-steel-dark mb-1">Familia</label>
                  <select
                    value={formData.family_id}
                    onChange={(e) => setFormData({ ...formData, family_id: e.target.value })}
                    className="input-industrial"
                    data-testid="product-family-select"
                  >
                    <option value="">— Sin familia —</option>
                    {families.map(f => (
                      <option key={f.id} value={f.id}>{f.nombre}</option>
                    ))}
                  </select>
                  {families.length === 0 && (
                    <p className="text-xs text-steel-gray mt-1">
                      No hay familias. Créalas en{' '}
                      <a href="/admin/familias" className="text-accent-primary underline">Familias</a>.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-steel-dark mb-1">Orden</label>
                  <input
                    type="number"
                    value={formData.orden}
                    onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 99 })}
                    className="input-industrial"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.activo}
                      onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-steel-dark">Activo</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="btn-outline">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Imagen</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Categoría</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Orden</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-steel-gray uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-4 py-4">
                      <div className="animate-pulse flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-200 rounded" />
                        <div className="flex-1 h-4 bg-gray-200 rounded" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-steel-gray">
                    No hay productos registrados
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <React.Fragment key={product.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <img
                          src={resolveImage(product.ruta_img, PRODUCT_IMAGE_BASE) || 'https://placehold.co/100x100/1A1A2E/E63946?text=NA'}
                          alt={product.nombre}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.target.src = 'https://placehold.co/100x100/1A1A2E/E63946?text=NA'; }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-steel-dark">{product.nombre}</p>
                        <p className="text-xs text-steel-gray">{product.seo_url}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {product.codigo ? (
                          <span className="font-mono text-accent-primary font-bold bg-accent-primary/10 px-2 py-0.5 rounded text-xs">{product.codigo}</span>
                        ) : (
                          <span className="text-steel-gray">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-steel-gray">{product.categoria || '-'}</td>
                      <td className="px-4 py-3 text-sm text-steel-gray">{product.orden}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${product.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {product.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={() => setExpandedId(expandedId === product.id ? null : product.id)}
                            className={`p-2 rounded transition-colors ${expandedId === product.id ? 'text-v2-orange bg-orange-50' : 'text-amber-600 hover:bg-amber-50'}`}
                            title="Variaciones"
                            data-testid={`variations-toggle-${product.id}`}
                          >
                            <Package size={16} />
                          </button>
                          <button 
                            onClick={() => handleEdit(product)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === product.id && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <VariationManager productId={product.id} productName={product.nombre} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
;
